import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
  Switch,
} from '@/components/ui';
import { RadioRow } from '@/features/shippingFee/components/RadioRow';
import { ExposedMallSelector } from './ExposedMallSelector';
import {
  CURRENCIES,
  CURRENCY_LABELS,
  DEFAULT_BANK_ACCOUNT_PAYLOAD,
  type BankAccountDetail,
  type BankAccountPayload,
  type Currency,
  type ExposedMall,
} from '../types/bankAccount';

interface BankAccountEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 편집 대상(상세). null 이면 신규. */
  target: BankAccountDetail | null;
  saving: boolean;
  onSubmit: (payload: BankAccountPayload) => void;
}

interface EditorDraft {
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  enabled: boolean;
  exposedMalls: ExposedMall[];
  currency: Currency;
  swiftCode: string;
  branchName: string;
  /** 입력 보존을 위해 문자열로 관리. 제출 시 정수화. */
  sortOrder: string;
}

const emptyDraft = (): EditorDraft => ({
  bankName: '',
  accountNumber: '',
  accountHolder: '',
  enabled: DEFAULT_BANK_ACCOUNT_PAYLOAD.enabled,
  exposedMalls: [...DEFAULT_BANK_ACCOUNT_PAYLOAD.exposedMalls],
  currency: DEFAULT_BANK_ACCOUNT_PAYLOAD.currency,
  swiftCode: '',
  branchName: '',
  sortOrder: '0',
});

const toDraft = (target: BankAccountDetail): EditorDraft => ({
  bankName: target.bankName,
  accountNumber: target.accountNumber,
  accountHolder: target.accountHolder,
  enabled: target.enabled,
  exposedMalls: [...target.exposedMalls],
  currency: target.currency,
  swiftCode: target.swiftCode ?? '',
  branchName: target.branchName ?? '',
  sortOrder: String(target.sortOrder),
});

const toInt = (raw: string): number => {
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) ? n : 0;
};

/**
 * 무통장 입금 계좌 생성/편집 다이얼로그. (M-4 ① 52 그리드, qa-52 deposits-admin.png.)
 * 은행명/계좌번호/예금주/지점명/SWIFT/통화/노출상점(다중)/사용토글/정렬순서.
 * PUT 은 전체교체(be-order 계약)라 폼이 모든 필드를 항상 제출한다. 검증은 필수
 * 3종(은행명/계좌번호/예금주)+노출상점 1개 이상이며, 최종 권위는 BE 가 판정한다.
 */
export const BankAccountEditor = ({
  open,
  onOpenChange,
  target,
  saving,
  onSubmit,
}: BankAccountEditorProps) => {
  const [draft, setDraft] = useState<EditorDraft>(() =>
    target ? toDraft(target) : emptyDraft(),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(target ? toDraft(target) : emptyDraft());
      setError(null);
    }
  }, [open, target]);

  const set = <K extends keyof EditorDraft>(key: K, val: EditorDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: val }));

  const validate = (): string | null => {
    if (draft.bankName.trim() === '') return '은행명을 입력하세요.';
    if (draft.accountNumber.trim() === '') return '계좌번호를 입력하세요.';
    if (draft.accountHolder.trim() === '') return '예금주를 입력하세요.';
    if (draft.exposedMalls.length === 0) return '노출상점을 1개 이상 선택하세요.';
    return null;
  };

  const handleSubmit = () => {
    const message = validate();
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    const swift = draft.swiftCode.trim();
    const branch = draft.branchName.trim();
    onSubmit({
      bankName: draft.bankName.trim(),
      accountNumber: draft.accountNumber.trim(),
      accountHolder: draft.accountHolder.trim(),
      enabled: draft.enabled,
      exposedMalls: draft.exposedMalls,
      currency: draft.currency,
      // 선택 입력 — 빈 값은 null 로 전송.
      swiftCode: swift === '' ? null : swift,
      branchName: branch === '' ? null : branch,
      sortOrder: toInt(draft.sortOrder),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[720px]">
        <DialogHeader>
          <DialogTitle>무통장 입금 계좌 {target ? '수정' : '등록'}</DialogTitle>
          <DialogDescription>
            결제 단계에서 안내되는 무통장(직접입금) 계좌입니다. 노출상점·통화에 맞는
            계좌만 해당 몰 결제 화면에 노출됩니다. 해외 송금 안내가 필요하면 SWIFT 를
            입력하세요.
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[66vh] flex-col gap-1 overflow-auto py-1">
          {error ? (
            <div
              className="mb-2 rounded-md px-3 py-2 text-[13px]"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <FormSection label="은행명" density="compact" required>
            <Input
              value={draft.bankName}
              onChange={(e) => set('bankName', e.target.value)}
              placeholder="예: 국민은행"
              maxLength={60}
              disabled={saving}
              data-testid="bank-account-bank-name"
            />
          </FormSection>

          <FormSection label="계좌번호" density="compact" required>
            <Input
              value={draft.accountNumber}
              onChange={(e) => set('accountNumber', e.target.value)}
              placeholder="예: 123456-01-789012"
              maxLength={50}
              disabled={saving}
              data-testid="bank-account-number"
            />
          </FormSection>

          <FormSection label="예금주" density="compact" required>
            <Input
              value={draft.accountHolder}
              onChange={(e) => set('accountHolder', e.target.value)}
              placeholder="예: (주)에버라인"
              maxLength={60}
              disabled={saving}
              data-testid="bank-account-holder"
            />
          </FormSection>

          <FormSection label="지점명" density="compact">
            <Input
              value={draft.branchName}
              onChange={(e) => set('branchName', e.target.value)}
              placeholder="예: 강남지점 (선택)"
              maxLength={60}
              disabled={saving}
              data-testid="bank-account-branch-name"
            />
          </FormSection>

          <FormSection label="SWIFT 코드" density="compact">
            <Input
              value={draft.swiftCode}
              onChange={(e) => set('swiftCode', e.target.value)}
              placeholder="해외 송금용 (선택)"
              maxLength={20}
              disabled={saving}
              className="max-w-[240px]"
              data-testid="bank-account-swift-code"
            />
          </FormSection>

          <RadioRow
            name="bank-account-currency"
            label="통화"
            options={CURRENCIES}
            labels={CURRENCY_LABELS}
            value={draft.currency}
            onChange={(v) => set('currency', v)}
            disabled={saving}
          />

          <ExposedMallSelector
            value={draft.exposedMalls}
            onChange={(v) => set('exposedMalls', v)}
            disabled={saving}
            required
          />

          <FormSection label="사용 여부" density="compact">
            <Switch
              checked={draft.enabled}
              onCheckedChange={(v) => set('enabled', v)}
              disabled={saving}
              aria-label="사용 여부"
              data-testid="bank-account-enabled"
            />
          </FormSection>

          <FormSection label="정렬순서" density="compact">
            <Input
              type="number"
              min={0}
              inputMode="numeric"
              value={draft.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
              disabled={saving}
              className="max-w-[120px] tabular-nums"
              data-testid="bank-account-sort-order"
            />
          </FormSection>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            취소
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={saving}
            data-testid="bank-account-save"
          >
            {saving ? '저장 중…' : '저장'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
